import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Search, Sparkles, Mic, MapPin, PenLine, Home as HomeIcon, KeyRound, Trees, Building2,
  Grid2x2, DollarSign, BedDouble, SlidersHorizontal, ArrowRight, Bot,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SearchOverlay from "@/components/search/SearchOverlay";
import { addRecentSearch } from "@/lib/searchHistory";
import { askAbayAI, openAbayAI } from "@/lib/abayAiBus";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

const HERO_IMG_BASE =
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=webp&q=80";
const HERO_IMAGE = `${HERO_IMG_BASE}&w=1280`;
const HERO_SRCSET = `${HERO_IMG_BASE}&w=640 640w, ${HERO_IMG_BASE}&w=1080 1080w, ${HERO_IMG_BASE}&w=1600 1600w, ${HERO_IMG_BASE}&w=1920 1920w`;

const MODE_TABS = [
  { key: "buy", label: "Buy", icon: HomeIcon, route: "/buy" },
  { key: "rent", label: "Rent", icon: KeyRound, route: "/rent" },
  { key: "land", label: "Land", icon: Trees, route: "/land" },
  { key: "commercial", label: "Commercial", icon: Building2, route: "/commercial" },
];

const FILTER_CHIPS = [
  { key: "types", label: "Types", icon: Grid2x2 },
  { key: "price", label: "Price", icon: DollarSign },
  { key: "beds", label: "Beds & Baths", icon: BedDouble },
  { key: "filters", label: "Filters", icon: SlidersHorizontal },
];

const AI_SUGGESTIONS = [
  "Best places to buy a home in Addis?",
  "How much is my property worth?",
  "Mortgage options for me",
  "Latest real estate trends",
];

export const Hero = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useTranslation();
  const firstName = (profile?.full_name || "").trim().split(" ")[0];
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 500], [0, 70]);
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("buy");
  const [searchOpen, setSearchOpen] = useState(false);

  const buildParams = (extra = {}) => {
    const p = new URLSearchParams();
    if (query.trim()) p.set("q", query.trim());
    if (mode && mode !== "buy") p.set("mode", mode);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  const runSearch = () => navigate(`/map-search${buildParams()}`);

  const submitSearch = (v) => {
    const s = (v || "").trim();
    if (s) { setQuery(s); try { addRecentSearch(s); } catch (e) { void e; } }
    setSearchOpen(false);
    const p = new URLSearchParams();
    if (s) p.set("q", s);
    if (mode && mode !== "buy") p.set("mode", mode);
    const qs = p.toString();
    navigate(`/map-search${qs ? `?${qs}` : ""}`);
  };

  const belowActions = [
    { key: "near", label: "Near Me", icon: MapPin, onClick: () => navigate("/map-search?near=1") },
    { key: "draw", label: "Draw on Map", icon: PenLine, onClick: () => navigate("/map-search?view=map&draw=1") },
  ];

  return (
    <section className="relative isolate overflow-hidden bg-[#0B1A3A]" data-testid="home-hero">
      {/* Full-width luxury background (parallax) + left dark-blue gradient overlay */}
      <motion.div className="absolute inset-0 will-change-transform" style={{ y: imgY }}>
        <img src={HERO_IMAGE} srcSet={HERO_SRCSET} sizes="100vw" width="1920" height="1080" alt="Luxury property in Ethiopia" fetchPriority="high" decoding="async" className="h-[116%] w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, rgba(9,20,48,0.96) 0%, rgba(13,27,64,0.9) 42%, rgba(13,27,64,0.62) 68%, rgba(13,27,64,0.34) 100%)" }} />
      <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: "linear-gradient(to top, hsl(var(--background)), transparent)" }} />

      <div className="abay-container relative pb-12 pt-10 sm:pt-12 lg:pb-16 lg:pt-16">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12">
          {/* ================= LEFT — copy + advanced search ================= */}
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
              {/* Desktop marketing badge (approved — unchanged) */}
              <span className="hidden items-center gap-1.5 rounded-full bg-white/12 px-3 py-1 text-white ring-1 ring-white/20 backdrop-blur-sm lg:inline-flex lg:text-xs">
                <Sparkles className="h-3.5 w-3.5 text-[#34D399]" /> Ethiopia&apos;s Smartest Property Platform
              </span>
              {/* Mobile welcome badge */}
              <span data-testid="hero-welcome-badge" className="inline-flex items-center gap-1.5 rounded-full bg-black/30 px-4 py-1.5 text-[12px] font-semibold text-white ring-1 ring-white/15 backdrop-blur-sm lg:hidden">
                <Sparkles className="h-3.5 w-3.5 text-[#34D399]" /> {firstName ? `${t("common.welcome")}, ${firstName}` : t("common.welcomeTo")}
              </span>
              <h1 data-testid="home-hero-headline" className="mt-4 text-[2rem] font-extrabold leading-[1.1] tracking-tight text-white sm:text-[2.6rem] lg:text-[3.1rem]">
                {t("hero.title")} <span className="text-[#34D399]">{t("hero.ethiopia")}</span>
              </h1>
              {/* Desktop subtitle (approved — unchanged) */}
              <p className="mt-3 hidden max-w-xl text-sm leading-relaxed text-white/80 sm:text-base lg:block">
                Buy, rent, sell and invest with confidence using AI-powered real estate technology.
              </p>
              {/* Mobile subtitle */}
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/85 lg:hidden">
                {t("hero.subtitle")}
              </p>
            </motion.div>

            {/* ===== MOBILE simplified search card (lg:hidden) ===== */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.08 }}
              data-testid="home-search-card-mobile"
              className="relative z-10 mt-6 w-full rounded-[20px] border border-white/50 bg-white/95 p-4 shadow-[0_24px_60px_rgba(8,15,40,0.35)] backdrop-blur-xl dark:border-white/10 dark:bg-[#0e1730]/95 lg:hidden"
            >
              {/* Row 1 — search field + voice + blue Search button */}
              <div className="flex items-center gap-2.5">
                <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 dark:border-white/10 dark:bg-white/5">
                  <Search className="h-5 w-5 shrink-0 text-[#1E3A8A] dark:text-[#7AA0FF]" />
                  <button
                    type="button"
                    data-testid="hero-mobile-search-input"
                    onClick={() => setSearchOpen(true)}
                    className="min-w-0 flex-1 truncate py-4 text-left text-[15px] outline-none"
                  >
                    <span className={query ? "text-slate-900 dark:text-slate-100" : "text-slate-400 dark:text-slate-500"}>
                      {query || t("hero.searchPlaceholder")}
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid="hero-mobile-search-mic"
                    aria-label="Voice search"
                    onClick={() => setSearchOpen(true)}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors duration-200 hover:bg-slate-100 hover:text-[#1E3A8A] dark:text-slate-300 dark:hover:bg-white/10"
                  >
                    <Mic className="h-5 w-5" />
                  </button>
                </div>
                <Button
                  onClick={runSearch}
                  data-testid="hero-mobile-search-submit"
                  aria-label="Search properties"
                  className="tap-press h-[56px] w-[56px] shrink-0 rounded-xl bg-[#1E3A8A] p-0 text-white shadow-[0_12px_28px_rgba(30,58,138,0.28)] transition-colors duration-200 hover:bg-[#17307a]"
                >
                  <Search className="h-5 w-5" />
                </Button>
              </div>
            </motion.div>

            {/* ===== DESKTOP ADVANCED SEARCH CARD (approved — unchanged) ===== */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.08 }}
              data-testid="home-search-card"
              className="relative z-10 mt-6 hidden w-full rounded-2xl border border-white/50 bg-white/95 p-4 shadow-[0_24px_60px_rgba(8,15,40,0.35)] backdrop-blur-xl dark:border-white/10 dark:bg-[#0e1730]/95 sm:p-6 lg:block"
            >
              {/* Mode tabs */}
              <Tabs value={mode} onValueChange={setMode} data-testid="hero-search-tabs">
                <TabsList className="grid h-auto w-full grid-cols-4 gap-1 rounded-xl bg-slate-100 p-1 dark:bg-white/5">
                  {MODE_TABS.map((t) => (
                    <TabsTrigger
                      key={t.key}
                      value={t.key}
                      data-testid={`hero-search-tab-${t.key}`}
                      className="flex items-center justify-center gap-1.5 rounded-lg py-2 text-[13px] font-semibold text-slate-600 transition-colors duration-200 data-[state=active]:bg-white data-[state=active]:text-[#1E3A8A] data-[state=active]:shadow-sm dark:text-slate-300 dark:data-[state=active]:bg-white/10 dark:data-[state=active]:text-white sm:text-sm"
                    >
                      <t.icon className="h-4 w-4" /> <span className="hidden sm:inline">{t.label}</span><span className="sm:hidden">{t.label}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              {/* Search input */}
              <div className="mt-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 dark:border-white/10 dark:bg-white/5">
                <Search className="h-5 w-5 shrink-0 text-[#1E3A8A] dark:text-[#7AA0FF]" />
                <button
                  type="button"
                  data-testid="hero-search-query-input"
                  onClick={() => setSearchOpen(true)}
                  className="flex-1 truncate py-3.5 text-left text-[15px] outline-none"
                >
                  <span className={query ? "text-slate-900 dark:text-slate-100" : "text-slate-400 dark:text-slate-500"}>
                    {query || "Search by city, neighborhood, address or landmark…"}
                  </span>
                </button>
                <button
                  type="button"
                  data-testid="hero-search-mic-button"
                  aria-label="Voice search"
                  onClick={() => setSearchOpen(true)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-500 transition-colors duration-200 hover:bg-slate-100 hover:text-[#1E3A8A] dark:text-slate-300 dark:hover:bg-white/10"
                >
                  <Mic className="h-5 w-5" />
                </button>
              </div>

              {/* Filter chips */}
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {FILTER_CHIPS.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`hero-search-filter-${c.key}`}
                    onClick={() => navigate("/map-search?filters=1")}
                    className="tap-press flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white py-2.5 text-[13px] font-semibold text-slate-700 transition-colors duration-200 hover:border-[#1E3A8A]/30 hover:bg-[#1E3A8A]/5 hover:text-[#1E3A8A] dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                  >
                    <c.icon className="h-4 w-4" /> {c.label}
                  </button>
                ))}
              </div>

              {/* Secondary actions */}
              <div className="mt-2 grid grid-cols-2 gap-2">
                {belowActions.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    data-testid={`hero-search-${a.key === "near" ? "near-me" : "draw-on-map"}`}
                    onClick={a.onClick}
                    className="tap-press flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white py-2.5 text-[13px] font-semibold text-[#1E3A8A] transition-colors duration-200 hover:bg-[#1E3A8A]/5 dark:border-white/10 dark:bg-white/5 dark:text-[#7AA0FF] dark:hover:bg-white/10 sm:text-sm"
                  >
                    <a.icon className="h-4 w-4" /> {a.label}
                  </button>
                ))}
              </div>

              {/* Primary CTA */}
              <Button
                onClick={runSearch}
                data-testid="hero-search-submit-button"
                className="tap-press mt-3 h-12 w-full rounded-xl bg-[#1E3A8A] text-[15px] font-semibold text-white shadow-[0_12px_28px_rgba(30,58,138,0.28)] transition-[box-shadow,filter] duration-200 hover:bg-[#17307a] hover:shadow-[0_16px_34px_rgba(30,58,138,0.34)] active:brightness-95"
              >
                <Search className="mr-2 h-5 w-5" /> Search Properties
              </Button>
            </motion.div>
          </div>

          {/* ================= RIGHT — Ask Abay AI panel (desktop only) ================= */}
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.12 }}
            data-testid="hero-ask-ai-panel"
            className="relative hidden overflow-hidden rounded-2xl border border-white/10 bg-[#0c1730]/85 p-6 shadow-[var(--shadow-lg)] backdrop-blur-xl lg:col-span-5 lg:block"
          >
            {/* soft glow */}
            <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#10B981]/20 blur-3xl" />
            <div className="relative flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-[#34D399]" />
                  <h2 className="text-xl font-bold text-white">Ask Abay AI</h2>
                </div>
                <p className="mt-1.5 max-w-[16rem] text-sm text-white/70">
                  Your smart real estate assistant for any questions.
                </p>
              </div>
              {/* Mascot */}
              <div className="relative flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                <Bot className="h-11 w-11 text-white" strokeWidth={1.6} />
                <span className="absolute bottom-2 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-[#34D399] shadow-[0_0_10px_2px_rgba(52,211,153,0.7)]" />
              </div>
            </div>

            <div className="relative mt-5 flex flex-col gap-2">
              {AI_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid="hero-ask-ai-suggestion-chip"
                  onClick={() => askAbayAI(s)}
                  className="group flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-left text-sm font-medium text-white/90 transition-colors duration-200 hover:border-white/20 hover:bg-white/10"
                >
                  {s}
                  <ArrowRight className="h-4 w-4 shrink-0 text-white/50 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-white" />
                </button>
              ))}
            </div>

            <Button
              onClick={() => openAbayAI()}
              data-testid="hero-ask-ai-button"
              className="abay-ask-gradient tap-press mt-5 h-12 w-full rounded-xl text-[15px] font-semibold text-white shadow-[0_12px_28px_rgba(16,185,129,0.28)] transition-[filter,box-shadow] duration-200 hover:brightness-105"
            >
              <Sparkles className="mr-2 h-5 w-5" /> Ask AI
            </Button>
          </motion.aside>
        </div>
      </div>

      <SearchOverlay open={searchOpen} initialValue={query} onClose={() => setSearchOpen(false)} onSubmit={submitSearch} />
    </section>
  );
};
